import type { JSX, ReactNode } from 'react';
import clsx from 'clsx';

/**
 * Standardized page header. Replaces the copy-pasted
 * `<header className="page-header">` block on every page.
 *
 * Props:
 *   - title: page title (required)
 *   - subtitle: optional muted strapline below the title
 *   - icon: optional icon component rendered to the left of the title
 *           in a tinted square
 *   - accent: tailwind-400 hex color the icon square uses (default indigo)
 *   - actions: optional ReactNode rendered to the right (refresh button,
 *              create button, filters, etc.)
 *
 * The page-header CSS class is preserved so existing layouts that
 * adjacent-margin to it keep working.
 */
export function PageHeader({
  title,
  subtitle,
  icon: Icon,
  accent = '#818cf8',
  actions,
  className,
}: {
  title: string;
  subtitle?: ReactNode;
  icon?: React.ComponentType<{ width?: number; height?: number; color?: string }>;
  accent?: string;
  actions?: ReactNode;
  className?: string;
}): JSX.Element {
  return (
    <header className={clsx('page-header', className)}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, minWidth: 0 }}>
        {Icon && (
          <div
            aria-hidden
            style={{
              flex: 'none',
              width: 36,
              height: 36,
              borderRadius: 10,
              background: `${accent}1f`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: `0 0 0 1px ${accent}33`,
            }}
          >
            <Icon width={18} height={18} color={accent} />
          </div>
        )}
        <div style={{ minWidth: 0 }}>
          <h2 style={{ marginBottom: 0 }}>{title}</h2>
          {subtitle && (
            <p className="muted" style={{ marginTop: 4 }}>{subtitle}</p>
          )}
        </div>
      </div>
      {actions && <div className="page-header__actions">{actions}</div>}
    </header>
  );
}
